// src/data/organizationsHelpers.js
//
// Thin wrappers around the organizations/{orgId} collection introduced
// by ticket 09. Shape per data-model doc §2.1:
//
//   {
//     name: string,
//     status: 'pending' | 'approved' | 'rejected' | 'suspended',
//     createdBy: uid,
//     createdAt: Timestamp,
//     decidedBy?: uid,
//     decidedAt?: Timestamp,
//   }
//
// Phase-2 tickets (13, 14, 15) use these helpers. Phase 1 introduces
// the collection as empty scaffolding — no UI calls it yet.

import {
  collection,
  doc,
  setDoc,
  getDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

const ORGANIZATIONS = 'organizations';

export const ORG_STATUSES = Object.freeze({
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  SUSPENDED: 'suspended',
});

/**
 * Creates a new organization in `pending` status. Returns the generated
 * orgId. Intended to be called from the self-serve signup flow (ticket 14)
 * — rules from ticket 11 gate this on status === 'pending' and
 * createdBy === request.auth.uid.
 *
 * Phase 1 callers (rehearsal, tests) can pass their own orgId for
 * determinism via the `id` option.
 */
export async function createOrganization({ name, createdBy, id }) {
  if (!name || !createdBy) {
    throw new Error('createOrganization requires name and createdBy');
  }
  const ref = id
    ? doc(db, ORGANIZATIONS, id)
    : doc(collection(db, ORGANIZATIONS));
  const payload = {
    name,
    status: ORG_STATUSES.PENDING,
    createdBy,
    createdAt: serverTimestamp(),
  };
  await setDoc(ref, payload);
  return ref.id;
}

export async function getOrganization(orgId) {
  const snap = await getDoc(doc(db, ORGANIZATIONS, orgId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * List orgs filtered by status. Used by the super-admin dashboard
 * (ticket 15) to render pending / approved / suspended tabs.
 */
export async function listOrganizationsByStatus(status) {
  const q = query(collection(db, ORGANIZATIONS), where('status', '==', status));
  const snap = await getDocs(q);
  const out = [];
  snap.forEach((d) => out.push({ id: d.id, ...d.data() }));
  out.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  return out;
}

/**
 * Super-admin action: flip an organization's status. Strict rules only
 * allow the write when the caller has `superAdmin` on their token.
 */
export async function setOrganizationStatus(orgId, nextStatus, { decidedBy }) {
  if (!orgId || !nextStatus) {
    throw new Error('setOrganizationStatus requires orgId and nextStatus');
  }
  const ref = doc(db, ORGANIZATIONS, orgId);
  await setDoc(
    ref,
    {
      status: nextStatus,
      decidedBy: decidedBy || null,
      decidedAt: serverTimestamp(),
    },
    { merge: true },
  );
}
